import React, { useState, useEffect } from "react";
import { isPhoneValid } from "../util/isPhoneValid";
import ChatDev from "./ChatDev";
import { VscCloseAll } from "react-icons/vsc";
import { motion } from "framer-motion";
import handelLead from "../Api/handellead";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";

const UserFormDetails = () => {
  const { userId, botID } = useParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: "+1",
  });

  const [countryCodes, setCountryCodes] = useState([]);
  const [errors, setErrors] = useState({});
  const [visible, setVisible] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const lead_user = localStorage.getItem("lead_user");
    try {
      const data = lead_user ? JSON.parse(lead_user) : {};
      if (data.name || data.email || data.phone_no) {
        setVisible(false);
      }
    } catch (err) {
      console.error("Invalid lead_user in localStorage", err);
    }
  }, []);

  useEffect(() => {
    fetch("/country_codes.json")
      .then((res) => res.json())
      .then((data) => setCountryCodes(data))
      .catch((err) => console.error("Failed to load country codes:", err));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.email.includes("@") || !form.email.includes("."))
      newErrors.email = "Invalid email";

    const sanitizedPhone = `${form.countryCode}${form.phone}`.replace(
      /\s+/g,
      ""
    );
    if (!isPhoneValid(sanitizedPhone)) newErrors.phone = "Invalid phone number";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    const sanitizedPhone = `${form.countryCode}${form.phone}`.replace(
      /\s+/g,
      ""
    );

    try {
      const result = await handelLead(
        userId,
        botID,
        form.name,
        form.email,
        sanitizedPhone
      );
      if (result !== false) {
        setVisible(false);
      } else {
        toast.error("Failed to submit lead. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred while submitting. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ y: "100%", opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 2, duration: 0.5, ease: "easeOut" }}
      className="fixed bottom-0 z-50 w-full p-6 bg-gradient-to-tr from-gray-800 via-gray-900 to-black text-white shadow-2xl border-t border-gray-700">
      {/* Close Button */}
      <button
        type="button"
        className="absolute top-3 right-3 hover:rotate-180 transition"
        onClick={() => setVisible(false)}>
        <VscCloseAll className="w-7 h-7 fill-red-500 hover:scale-110" />
      </button>

      <div className="flex flex-wrap gap-4 items-start justify-center">
        {["name", "email"].map((field) => (
          <div
            key={field}
            className="flex flex-col w-full sm:w-1/4 bg-white bg-opacity-10 p-4 rounded-xl shadow-md">
            <label
              htmlFor={field}
              className="mb-1 text-sm font-semibold capitalize text-blue-300">
              {field}:
            </label>
            <input
              id={field}
              type={field === "email" ? "email" : "text"}
              name={field}
              value={form[field]}
              onChange={handleChange}
              placeholder={`Enter your ${field}`}
              className={`rounded-lg p-2 text-black focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all ${
                errors[field]
                  ? "border border-red-500"
                  : "border border-gray-300"
              }`}
            />
            {errors[field] && (
              <span className="text-red-400 text-xs mt-1">{errors[field]}</span>
            )}
          </div>
        ))}

        {/* Phone Number */}
        <div className="flex flex-col w-full sm:w-1/3 bg-white bg-opacity-10 p-4 rounded-xl shadow-md">
          <label
            htmlFor="phone"
            className="mb-1 text-sm font-semibold text-blue-300">
            Phone Number:
          </label>
          <div className="flex gap-2">
            <select
              name="countryCode"
              value={form.countryCode}
              onChange={handleChange}
              className="text-black rounded-lg p-2 max-w-[25%] border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400">
              {countryCodes.map((code) => (
                <option key={code.code} value={code.dial_code}>
                  {`${code.flag} ${code.dial_code} - ${code.name}`}
                </option>
              ))}
            </select>
            <input
              id="phone"
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="1234567890"
              className={`w-full rounded-lg p-2 tracking-widest text-black focus:outline-none focus:ring-2 focus:ring-blue-400 transition ${
                errors.phone
                  ? "border border-red-500"
                  : "border border-gray-300"
              }`}
            />
          </div>
          {errors.phone && (
            <span className="text-red-400 text-xs mt-1">{errors.phone}</span>
          )}
        </div>

        {/* Submit */}
        <div className="w-full sm:w-1/3">
          <button
            type="submit"
            disabled={loading}
            className={`bg-blue-600 w-full text-white px-6 py-2 rounded-lg transition font-semibold shadow-lg ${
              loading ? "opacity-60 cursor-not-allowed" : "hover:bg-blue-700"
            }`}>
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>

      {/* Chat UI */}
      <div className="w-full mt-6 invert">
        <ChatDev />
      </div>
    </motion.form>
  );
};

export default UserFormDetails;
